import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import type { VendorPickupInput } from '@terra/api-client';
import { Button, Card, CardBody, Input } from '@terra/ui';
import { INDIAN_STATE_OPTIONS } from '@terra/utils';

import { api } from '../lib/api';

export const PICKUP_QUERY_KEY = ['vendor-pickup'] as const;

const EMPTY: VendorPickupInput = {
  line1: '',
  line2: '',
  landmark: '',
  city: '',
  stateCode: '',
  pincode: '',
  contactName: '',
  contactPhone: '',
};

const STATUS_COPY = {
  SYNCED: {
    tone: 'border-emerald-200 bg-emerald-50 text-emerald-800',
    text: 'Ready for pickup — couriers can collect from this address.',
  },
  PENDING_VERIFICATION: {
    tone: 'border-amber-200 bg-amber-50 text-amber-800',
    text: 'Registered with the courier. Its phone number is being verified — Terra Shoppy will confirm it with you.',
  },
  FAILED: {
    tone: 'border-rose-200 bg-rose-50 text-rose-800',
    text: 'The courier could not register this address yet. Our team has been shown why and will sort it out with you.',
  },
} as const;

/**
 * Where couriers collect this seller's parcels.
 *
 * Part of onboarding: the store cannot be approved without a complete pickup
 * address, and approval registers it with the courier — the seller never
 * deals with Shiprocket. Changing it later registers the new address as a new
 * pickup location; parcels already booked are still collected from the old one.
 */
export function PickupAddressCard() {
  const qc = useQueryClient();
  const pickup = useQuery({
    queryKey: PICKUP_QUERY_KEY,
    queryFn: () => api.vendors.getMyPickupAddress(),
  });
  const [form, setForm] = useState<VendorPickupInput>(EMPTY);
  const [error, setError] = useState('');
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    const w = pickup.data?.warehouse;
    if (!w) return;
    setForm({
      line1: w.line1 ?? '',
      line2: w.line2 ?? '',
      landmark: w.landmark ?? '',
      city: w.city ?? '',
      stateCode: w.stateCode ?? '',
      pincode: w.pincode ?? '',
      contactName: w.contactName ?? '',
      contactPhone: w.contactPhone ?? '',
    });
  }, [pickup.data?.warehouse]);

  const save = useMutation({
    mutationFn: () =>
      api.vendors.setMyPickupAddress({
        line1: form.line1.trim(),
        city: form.city.trim(),
        stateCode: form.stateCode,
        pincode: form.pincode.trim(),
        contactName: form.contactName.trim(),
        contactPhone: form.contactPhone.replace(/[\s-]/g, ''),
        ...(form.line2?.trim() ? { line2: form.line2.trim() } : {}),
        ...(form.landmark?.trim() ? { landmark: form.landmark.trim() } : {}),
      }),
    onSuccess: (data) => {
      qc.setQueryData(PICKUP_QUERY_KEY, data);
      void qc.invalidateQueries({ queryKey: ['vendor-store'] });
      setSaved(true);
      setError('');
    },
    onError: (e: unknown) => {
      setSaved(false);
      setError(e instanceof Error ? e.message : 'Could not save the pickup address');
    },
  });

  const set = (k: keyof VendorPickupInput) => (v: string) => {
    setSaved(false);
    setForm((f) => ({ ...f, [k]: v }));
  };

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (form.line1.trim().length < 10)
      return setError('Enter the house/shop number, building and street in address line 1.');
    if (!form.city.trim()) return setError('Enter the city.');
    if (!form.stateCode) return setError('Choose the state.');
    if (!/^\d{6}$/.test(form.pincode.trim())) return setError('Pincode must be 6 digits.');
    if (form.contactName.trim().length < 2) return setError('Enter the pickup contact name.');
    if (!/^(\+91)?[6-9]\d{9}$/.test(form.contactPhone.replace(/[\s-]/g, '')))
      return setError('Enter a 10-digit mobile number for the pickup contact.');
    save.mutate();
  };

  const status = pickup.data?.shiprocket.status ?? null;
  const problems = pickup.data?.problems ?? [];

  return (
    <Card className="rounded-xl border border-slate-100 shadow-lg">
      <CardBody className="space-y-4 p-5">
        <div>
          <h3 className="text-ink text-[15px] font-bold">Pickup address *</h3>
          <p className="mt-0.5 text-[12.5px] text-slate-500">
            Where couriers collect your orders. Use the full address of your shop or godown — it is
            checked with your KYC address proof, and your store cannot go live without it.
          </p>
        </div>

        {status && (
          <div
            className={`rounded-lg border px-3 py-2 text-[12.5px] font-medium ${STATUS_COPY[status].tone}`}
          >
            {STATUS_COPY[status].text}
          </div>
        )}
        {!status && problems.length > 0 && pickup.data?.warehouse && (
          <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-[12.5px] font-medium text-amber-800">
            {problems.join(' ')}
          </div>
        )}

        <form onSubmit={submit} className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            <Input
              label="Address line 1 *"
              placeholder="Shop/house no., building, street"
              value={form.line1}
              onChange={(e) => set('line1')(e.target.value)}
              maxLength={200}
            />
            <Input
              label="Address line 2"
              placeholder="Area, locality"
              value={form.line2 ?? ''}
              onChange={(e) => set('line2')(e.target.value)}
              maxLength={200}
            />
          </div>
          <div className="grid gap-4 md:grid-cols-4">
            <Input
              label="Landmark"
              placeholder="Near…"
              value={form.landmark ?? ''}
              onChange={(e) => set('landmark')(e.target.value)}
              maxLength={120}
            />
            <Input
              label="City *"
              placeholder="e.g. Indore"
              value={form.city}
              onChange={(e) => set('city')(e.target.value)}
              maxLength={80}
            />
            <div className="flex flex-col gap-1.5">
              <label className="text-ink text-[13px] font-semibold" htmlFor="pickup-state">
                State *
              </label>
              <select
                id="pickup-state"
                className="border-border-strong/40 bg-surface text-ink focus:border-gold-400 h-[42px] rounded-xl border px-3 text-sm focus:outline-none"
                value={form.stateCode}
                onChange={(e) => set('stateCode')(e.target.value)}
              >
                <option value="">Select state</option>
                {INDIAN_STATE_OPTIONS.map((s) => (
                  <option key={s.code} value={s.code}>
                    {s.name}
                  </option>
                ))}
              </select>
            </div>
            <Input
              label="Pincode *"
              placeholder="e.g. 452001"
              value={form.pincode}
              onChange={(e) => set('pincode')(e.target.value.replace(/\D/g, ''))}
              maxLength={6}
            />
          </div>
          <div className="grid gap-4 md:grid-cols-2">
            <Input
              label="Pickup contact name *"
              placeholder="Who hands over the parcel"
              value={form.contactName}
              onChange={(e) => set('contactName')(e.target.value)}
              maxLength={120}
            />
            <Input
              label="Pickup contact mobile *"
              placeholder="10-digit mobile"
              value={form.contactPhone}
              onChange={(e) => set('contactPhone')(e.target.value)}
              maxLength={14}
              hint="The courier calls this number, and it may be verified by OTP."
            />
          </div>

          {error && <p className="text-[12.5px] font-semibold text-rose-700">{error}</p>}
          {saved && !error && (
            <p className="text-[12.5px] font-semibold text-emerald-700">Pickup address saved.</p>
          )}

          <div className="flex justify-end">
            <Button type="submit" variant="primary" loading={save.isPending}>
              Save pickup address
            </Button>
          </div>
        </form>
      </CardBody>
    </Card>
  );
}
